import React, { useState, useEffect, useRef } from "react"
import { useTranslation } from 'react-i18next'
import { Link, useParams, useNavigate } from 'react-router'
import Select from 'react-select'
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap'

import { httpOk, GET, POST, PUT } from 'helpers/api'
import { formatDate, formatMoney } from 'helpers/helpers'
import HealthModuleCard from 'pages/main/setupHealth/HealthModuleCard'

// селект встроен в ячейку: без рамки и фона, чтобы читался как содержимое td
const tableSelectStyles = {
	control: (base) => ({
		...base,
		border: 'none',
		borderRadius: 0,
		minHeight: '32px',
		boxShadow: 'none',
		backgroundColor: 'transparent',
	}),
	valueContainer: (base) => ({ ...base, padding: '0 8px' }),
	indicatorsContainer: (base) => ({ ...base, padding: 0 }),
	indicatorSeparator: () => ({ display: 'none' }),
	dropdownIndicator: (base) => ({ ...base, padding: '0 6px' }),
	clearIndicator: (base) => ({ ...base, padding: '0 2px' }),
	menuPortal: (base) => ({ ...base, zIndex: 9999 }),
}

// Ставки комиссии агрегатора по тому, кто вёз заказ (AggregatorCommissionService на бэке)
const commissionFields = [
	{ key: 'commission_aggregator_percent', label: 'commission_aggregator_courier', hint: 'commission_aggregator_courier_hint' },
	{ key: 'commission_own_percent', label: 'commission_own_delivery', hint: 'commission_own_delivery_hint' },
	{ key: 'commission_pickup_percent', label: 'commission_pickup', hint: 'commission_pickup_hint' },
]

// в списке опций цена продажи справа, в выбранном значении — только название
function formatProductOption(option, { context }) {
	if (context === 'value') return option.name
	return (
		<div className="d-flex justify-content-between">
			<span>{option.name}</span>
			<span className="text-muted ms-2 text-nowrap">{formatMoney(option.sale_price)}</span>
		</div>
	)
}

function Index() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()

	const cardRef = useRef(null);

	const [data, setData] = useState({ 'poses': [] })
	const [copiedKey, setCopied] = useState(null);
	const [paymentTypes, setPaymentTypes] = useState([])
	const [externalMenu, setExternalMenu] = useState([])
	const [counterparties, setCounterparties] = useState([])
	const [externalProducts, setExternalProducts] = useState([])
	const [orderDeliveryTypes, setOrderDeliveryTypes] = useState([])
	const [externalIntegrationTypes, setExternalIntegrationTypes] = useState([])
	const [modalRefresh, setModalRefresh] = useState(false);
	// Блок «Проверка настроек» по этой интеграции: тот же, что на странице /setup-health.
	// Он здесь, чтобы ошибку видели там, где её чинят, а не только в общем разделе
	const [health, setHealth] = useState(null)

	async function searchWithFilter() {
		var filterUrl = `/external-integrations/${id}`

		var response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			const responsePoses = await GET('/helper/poses')
			if (responsePoses.data.length !== response.data.poses) {
				let missingItems = responsePoses.data.filter(item => !response.data.poses.some(existingItem => existingItem.id === item.id));
				response.data.poses.push(...missingItems);
			}

			const paymentTypesResponse = await GET('/helper/payment-types')
			const nextPaymentTypes = httpOk(paymentTypesResponse) ? (paymentTypesResponse.data || []) : []
			if (httpOk(paymentTypesResponse)) setPaymentTypes(nextPaymentTypes)

			const counterpartyId =
				response?.data?.counterparty_id ||
				resolveCounterpartyId(response?.data?.payment_type_id, nextPaymentTypes)

			const nextData = {
				...response.data,
				counterparty_id: counterpartyId,
			}
			setData(nextData)

			if (response.data.external_group_id) {
				const responseProducts = await GET(`/external-menu-products/${response.data.external_group_id}`)
				if (httpOk(responseProducts)) setExternalProducts(responseProducts.data)
			}

			if (counterpartyId) {
				getCounterparties(counterpartyId)
			} else {
				setCounterparties([])
			}
		}
	}

	async function createUpdate(e) {
		e.preventDefault()

		var response
		if (id) response = await PUT('/external-integrations', data, { loader: true })
		if (!id) {
			response = await POST('/external-integrations', data, { loader: true })
			if (httpOk(response)) navigate(-1)
		}

		if (httpOk(response)) { }
	}

	async function refreshOauthAccessToken() {
		var response
		if (data.id) response = await PUT('/external-integrations', data, { loader: true })

		if (httpOk(response)) toggleModalRefresh()
	}

	function handleSettingChange(e, item, index) {
		var dataCopy = { ...data }

		dataCopy['poses'][index]['status'] = e.target.checked
		setData(dataCopy)
	}

	// Ставка комиссии: пустое поле — null (у интеграции — «без комиссии», у точки — «как у интеграции»)
	function commissionValue(value) {
		return value === '' ? null : value
	}

	function handlePosCommission(posId, key, value) {
		setData((prev) => ({
			...prev,
			poses: prev.poses.map((pos) => pos.id === posId ? { ...pos, [key]: commissionValue(value) } : pos),
		}))
	}

	function addPromoPair() {
		const dataCopy = { ...data }
		dataCopy.products = dataCopy.products ?? []
		dataCopy.products.push({
			source_id: null,
			id: null,
		})
		setData(dataCopy)
	}

	function updatePromoPair(index, key, option) {
		const dataCopy = { ...data }
		dataCopy.products = dataCopy.products ?? []
		dataCopy.products[index] = {
			...dataCopy.products[index],
			[key]: option ? option.id : null,
		}
		setData(dataCopy)
	}

	function removePromoPair(index) {
		const dataCopy = { ...data }
		dataCopy.products = dataCopy.products ?? []
		dataCopy.products.splice(index, 1)
		setData(dataCopy)
	}

	async function handleSelectExternalMenu(option) {
		setData({ ...data, 'external_group_id': option ? option.id : null, 'products': [] })

		if (option && option.id) {
			const response = await GET(`/external-menu-products/${option.id}`)
			if (httpOk(response)) setExternalProducts(response.data)
		} else {
			setExternalProducts([])
		}
	}

	async function handlePaymentType(option) {
		const counterpartyId = option?.counterparty_id || null

		setData({
			...data,
			'payment_type_id': option ? option.payment_type_id : null,
			'counterparty_id': counterpartyId,
			'counterparty_entity_id': null,
		})

		if (counterpartyId) {
			getCounterparties(counterpartyId)
		} else {
			setCounterparties([])
		}
	}

	function resolveCounterpartyId(paymentTypeId, paymentTypesList = paymentTypes) {
		const paymentType = paymentTypesList.find(
			(option) => option.payment_type_id === paymentTypeId
		)
		return paymentType?.counterparty_id || null
	}

	function ensureCounterpartiesLoaded(nextData = data, paymentTypesList = paymentTypes) {
		const counterpartyId =
			nextData?.counterparty_id ||
			resolveCounterpartyId(nextData?.payment_type_id, paymentTypesList)

		if (!counterpartyId) {
			setCounterparties([])
			return null
		}

		if (!nextData?.counterparty_id) {
			setData((prev) => ({
				...prev,
				counterparty_id: counterpartyId,
			}))
		}

		getCounterparties(counterpartyId)
		return counterpartyId
	}

	function handleCopy(copyKey) {
		if (!cardRef.current) return;

		const text = cardRef.current.innerText;

		navigator.clipboard.writeText(text) // Копируем текст в буфер обмена
			.then(() => {
				setCopied(copyKey); // Устанавливаем индекс кнопки
				setTimeout(() => setCopied(null), 1000); // Убираем уведомление через 2 секунды
			})
			.catch((error) => {
				console.error("Ошибка копирования: ", error);
			});
	};

	function toggleModalRefresh(bool = false) {
		setModalRefresh(bool)
	}

	// Забирал ли агрегатор меню после последней правки: если нет — пора просить его перезагрузить
	function returnClassPullStatus(item) {
		if (!item.menu_changed_at) return ''
		if (!item.menu_pulled_at) return 'bg-secondary text-white'
		if (item.is_menu_outdated) return 'bg-warning text-white'
		return 'bg-success text-white'
	}

	function menuPullStatus(item) {
		if (!item.menu_changed_at) return '—'
		if (!item.menu_pulled_at) return t('menu_never_pulled')
		if (item.is_menu_outdated) return t('menu_changed_after_pull')
		return t('menu_up_to_date')
	}

	function menuPullTitle(item) {
		if (!item.menu_changed_at) return undefined
		return `${menuPullStatus(item)}. ${t('menu_changed_at')}: ${formatDate(item.menu_changed_at, 'dd.MM.yyyy HH:mm')}`
	}

	function renderJson() {
		return <div><pre>{JSON.stringify(data, null, 2)}</pre></div>;
	}

	async function getPaymentTypes() {
		const response = await GET('/helper/payment-types')
		if (httpOk(response)) setPaymentTypes(response.data)
	}

	async function getExternalMenus() {
		const response = await GET('/helper/external-menu')
		if (httpOk(response)) setExternalMenu(response.data)
	}

	async function getOrderDeliveryTypes() {
		const response = await GET('/helper/order-type-subs')
		if (httpOk(response)) setOrderDeliveryTypes(response.data)
	}

	async function getExternalIntegrationTypes() {
		const response = await GET(`/helper/delivery-providers`)
		if (httpOk(response)) setExternalIntegrationTypes(response.data)
	}

	async function getPoses() {
		const response = await GET('/helper/poses', {}, { loader: true })
		if (httpOk(response)) setData({ ...data, 'poses': response.data });
	}

	async function getCounterparties(counterparty_id) {
		var response = await GET(`/helper/counterparties/${counterparty_id}`, {}, { loader: true })

		if (httpOk(response)) setCounterparties(response.data)
	}

	// Проверки этой интеграции: ключи, точки, живое ли внешнее меню, доходят ли заказы
	async function getHealth() {
		const response = await GET(`/setup-health/delivery_${id}`, {}, { loader: false })
		if (httpOk(response)) setHealth(response.data)
	}

	useEffect(() => {
		if (id) {
			searchWithFilter()
			getHealth()
		} else {
			getPoses()
			getPaymentTypes()
		}
		getExternalMenus()
		getOrderDeliveryTypes()
		getExternalIntegrationTypes()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex text-truncate">
					<h4 className="vertical-center">
						{t('integrations')}
					</h4>
				</div>
				{id &&
					<div className="d-flex gap-2">
						<Link className="btn btn-outline-primary" to={`/external-integrations/check/${id}`}>
							<i className="uil uil-search-alt me-1"></i>
							{t('external_menu_check')}
						</Link>
					</div>
				}
			</div>

			{/* Проверка настроек этой интеграции — сверху, до формы: если внешнее меню
				отвязалось, знать об этом надо раньше, чем править поля */}
			{health && health.status !== 'ok' &&
				<HealthModuleCard module={health} defaultOpen={false} />
			}

			<div className="card">
				<div className="card-body">
					<div className="row">
						<div className="col-md-6">
							<div className="form-group">
								<label>{t('type')}<span className="required-mark">*</span></label>
								<Select
									options={externalIntegrationTypes}
									value={externalIntegrationTypes.find(option => option.id === data.integration_provider_id) || ''}
									onChange={(option) => setData({ ...data, 'integration_provider_id': option.id })}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
								/>
							</div>

							<div className="form-group">
								<label>{t('name2')}</label>
								<input type="text" className="form-control" name="name"
									autoComplete="off"
									value={data.name ?? ''}
									onChange={(e) => setData({ ...data, 'name': e.target.value })} />
							</div>
							<div className="form-group w-100">
								<label>{t("description")}</label>
								<textarea className="form-control" name="description"
									autoComplete="off"
									value={data.description ?? ''}
									onChange={(e) => setData({ ...data, 'description': e.target.value })} />
							</div>

							<hr />
							<div className="d-flex gap-2">
								<div className="form-group w-75">
									<label>Webhook URL</label>
									<input type="text" className="form-control" name="webhook"
										disabled
										value={data.webhook ?? ''}
										onChange={() => { }} />
								</div>
								<div className="form-group w-25">
									<label>prefix</label>
									<input type="text" className="form-control" name="prefix"
										value={data.prefix ?? ''}
										onChange={(e) => setData({ ...data, 'prefix': e.target.value })} />
								</div>
							</div>
							<div className="d-flex gap-2">
								<div className="form-group w-100">
									<label>Client ID</label>
									<input type="text" className="form-control" name="client_id"
										disabled
										value={data.client_id ?? ''}
										onChange={() => { }} />
								</div>
								<div className="form-group w-100">
									<label>Client Secret</label>
									<input type="text" className="form-control" name="client_secret"
										disabled
										value={data.client_secret ?? ''}
										onChange={() => { }} />
								</div>
							</div>

							<div className="card-code"
								ref={cardRef}
								style={{ 'position': 'relative' }}>
								<div
									style={{
										'top': '5px',
										'right': '5px',
										'position': 'absolute',
									}}>
									<button className="btn btn-sm btn-primary"
										title={t('copy')}
										style={{
											'width': '40px',
											'position': 'relative',
										}}
										onClick={() => handleCopy('credentials')}>
										<i className="uil uil-copy"></i>

										{copiedKey === 'credentials' && (
											<div style={{
												position: 'absolute',
												top: '-30px',
												left: '50%',
												transform: 'translateX(-50%)',
												background: 'black',
												color: 'white',
												padding: '5px 10px',
												borderRadius: '5px',
												fontSize: '12px',
												zIndex: 1000,
												opacity: 0.9,
											}}>
												{t('copied')}
											</div>
										)}
									</button>
								</div>

								<b>Webhook URL</b>
								<div>{data.webhook ?? ''}</div>

								<b>Client ID</b>
								<div>{data.client_id ?? ''}</div>

								<b>Client Secret</b>
								<div>{data.client_secret ?? ''}</div>
							</div>

							<hr />

							<div className="d-flex gap-2">
								<div className="form-group w-100">
									<label>{t('external_menu')}<span className="required-mark">*</span></label>
									<Select
										options={externalMenu}
										value={externalMenu.find(option => option.id === data.external_group_id) || ''}
										onChange={(option) => handleSelectExternalMenu(option)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
									/>
								</div>
								<div className="form-group w-100">
									<label>{t('payment_type')}<span className="required-mark">*</span></label>
									<Select
										options={paymentTypes}
										value={paymentTypes.find(option => option.payment_type_id === data.payment_type_id) || ''}
										onChange={(option) => handlePaymentType(option)}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.payment_type_id}
									/>
								</div>
							</div>

							<div className="form-group w-100">
								<label>{t('delivery_type')}<span className="required-mark">*</span></label>
								<Select
									options={orderDeliveryTypes}
									value={orderDeliveryTypes.find(option => option.id === data.order_type_sub_id) || ''}
									onChange={(option) => setData({ ...data, 'order_type_sub_id': option ? option.id : null })}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
								/>
							</div>

							<hr />
							<div className="d-flex justify-content-between mb-2">
								<div className="fw-500 vertical-center">
									{t('auto_cheque_confirmation')}
								</div>
								<div className="text-muted vertical-center">{t('auto_confirm_from_order_type')}</div>
							</div>
							<div className="d-flex justify-content-between mb-2">
								<div className="fw-500 vertical-center">
									{t('auto_cheque_deletion')}
								</div>
								<div className="form-check form-switch form-switch-lg">
									<input type="checkbox" className="form-check-input"
										name="is_auto_close_cheque"
										checked={data.is_auto_close_cheque ? true : false}
										onChange={(e) => setData({ ...data, 'is_auto_close_cheque': e.target.checked })} />
								</div>
							</div>
							<div className="d-flex justify-content-between mb-2 d-non">
								<div className="fw-500 vertical-center">
									{t('is_auto_payment')}
								</div>
								<div className="form-check form-switch form-switch-lg">
									<input type="checkbox" className="form-check-input"
										name="is_auto_payment"
										checked={data.is_auto_payment ? true : false}
										onChange={(e) => {
											const checked = e.target.checked
											const nextData = { ...data, is_auto_payment: checked }
											setData(nextData)
											if (checked) ensureCounterpartiesLoaded(nextData)
											if (!checked) setCounterparties([])
										}} />
								</div>
							</div>
							{data.is_auto_payment &&
								<div className="form-group">
									<label>{t('counterparty')}<span className="required-mark">*</span></label>
									{!data.counterparty_id &&
										<div className="text-danger small mb-2">
											У выбранного типа оплаты не задан тип контрагента в настройках типа оплаты.
										</div>
									}
									<Select
										options={counterparties}
										value={counterparties.find(option => option.id && option.id === data.counterparty_entity_id) || null}
										onChange={(option) => setData({ ...data, 'counterparty_entity_id': option ? option.id : null })}
										placeholder=""
										noOptionsMessage={() => t('list_empty')}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
										isDisabled={!data.counterparty_id}
									/>
								</div>
							}

							<hr />
							<div className="fw-500 mb-1">{t('aggregator_commission')}</div>
							<div className="text-muted small mb-2">
								{t('aggregator_commission_hint')}
								{data.commission_enabled_at &&
									<> {t('aggregator_commission_since')}: {formatDate(data.commission_enabled_at, 'dd.MM.yyyy HH:mm')}</>
								}
							</div>
							{!(data.is_auto_payment && data.counterparty_entity_id) &&
								<div className="text-danger small mb-2">
									{t('aggregator_commission_need_counterparty')}
								</div>
							}
							<div className="d-flex gap-2">
								{commissionFields.map((field) => (
									<div className="form-group w-100" key={field.key}>
										<label>{t(field.label)}</label>
										<input type="number" className="form-control"
											min="0" max="100" step="0.01"
											value={data[field.key] ?? ''}
											onChange={(e) => setData({ ...data, [field.key]: commissionValue(e.target.value) })} />
										<div className="text-muted small mt-1">{t(field.hint)}</div>
									</div>
								))}
							</div>

							{data.poses.some((pos) => pos.status) &&
								<>
									<label>{t('aggregator_commission_by_pos')}</label>
									<div className="table-responsive">
										<table className="table table-bordered mb-0">
											<thead>
												<tr>
													<th>{t('name2')}</th>
													{commissionFields.map((field) => (
														<th key={field.key} className="text-center" style={{ 'width': '22%' }}>{t(field.label)}</th>
													))}
												</tr>
											</thead>
											<tbody>
												{data.poses.filter((pos) => pos.status).map((pos) => (
													<tr key={pos.id}>
														<td className="vertical-middle">{pos.name}</td>
														{commissionFields.map((field) => (
															<td key={field.key} className="p-0">
																<input type="number" className="form-control border-0 text-center"
																	min="0" max="100" step="0.01"
																	placeholder={data[field.key] ?? ''}
																	value={pos[field.key] ?? ''}
																	onChange={(e) => handlePosCommission(pos.id, field.key, e.target.value)} />
															</td>
														))}
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</>
							}
						</div>

						<div className="col-md-6">
							<div className="d-flex gap-2">
								<div className="form-group w-100">
									<label>Vendor Client ID</label>
									<input type="text" className="form-control" name="vendor_client_id"
										value={data.vendor_client_id ?? ''}
										onChange={(e) => setData({ ...data, 'vendor_client_id': e.target.value })} />
								</div>
								<div className="form-group w-100">
									<label>Vendor Client Secret</label>
									<input type="text" className="form-control" name="vendor_client_secret"
										value={data.vendor_client_secret ?? ''}
										onChange={(e) => setData({ ...data, 'vendor_client_secret': e.target.value })} />
								</div>
							</div>

							<hr />

							<div className="form-group w-100">
								<div className="d-flex justify-content-between mb-2">
									<label className="mb-0">{t('discounts_and_promotions')}</label>
									<button
										type="button"
										className="btn btn-sm btn-primary"
										onClick={() => addPromoPair()}
										disabled={!externalProducts.length}
									>
										+
									</button>
								</div>
								<div className="table-responsive">
									<table className="table table-bordered mb-0" style={{ tableLayout: 'fixed' }}>
										<thead>
											<tr>
												<th style={{ width: '33px', minWidth: '33px' }} className="text-center">#</th>
												<th>{t('main_dish')}</th>
												<th>{t('gift_dish')}</th>
												<th style={{ width: '110px' }} className="text-end text-nowrap">{t('sale_price')}</th>
												<th style={{ width: '33px', minWidth: '33px' }}></th>
											</tr>
										</thead>
										<tbody>
											{(data.products ?? []).map((item, index) => (
												<tr key={index}
													title={item.created_at
														? `${t('promo_added_at')}: ${formatDate(item.created_at, 'dd.MM.yyyy HH:mm')}`
														: ''}>
													<td style={{ width: '33px' }} className="text-center">
														{index + 1}
													</td>
													<td className="p-0">
														<Select
															isClearable
															options={externalProducts}
															value={externalProducts.find(option => option.id === item.source_id || option.id === item.source_external_product_id) || null}
															onChange={(option) => updatePromoPair(index, 'source_id', option)}
															placeholder=""
															noOptionsMessage={() => t('list_empty')}
															getOptionLabel={(option) => option.name}
															getOptionValue={(option) => option.id}
																formatOptionLabel={formatProductOption}
															menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
															menuPosition="fixed"
															styles={tableSelectStyles}
														/>
													</td>
													<td className="p-0">
														<Select
															isClearable
															options={externalProducts}
															value={externalProducts.find(option => option.id === item.id) || null}
															onChange={(option) => updatePromoPair(index, 'id', option)}
															placeholder=""
															noOptionsMessage={() => t('list_empty')}
															getOptionLabel={(option) => option.name}
															getOptionValue={(option) => option.id}
																formatOptionLabel={formatProductOption}
															menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
															menuPosition="fixed"
															styles={tableSelectStyles}
														/>
													</td>
													<td className="text-end text-nowrap">
														{(() => {
															const gift = externalProducts.find(option => option.id === item.id)
															return gift ? formatMoney(gift.sale_price) : ''
														})()}
													</td>
													<td className="text-center" style={{ width: '33px' }}>
														<button
															type="button"
															className="btn btn-sm btn-link text-danger p-0"
															title={t('delete')}
															onClick={() => removePromoPair(index)}
														>
															<i className="uil-times" style={{ fontSize: '18px' }}></i>
														</button>
													</td>
												</tr>
											))}
											{!(data.products ?? []).length &&
												<tr>
													<td colSpan={5} className="text-center text-muted">
														{t('list_empty')}
													</td>
												</tr>
											}
										</tbody>
									</table>
								</div>
							</div>

							<hr />

							<div className="d-flex justify-content-between flex-wrap gap-2">
								<label>{t('poses')}</label>
								{data?.menu_changed_at &&
									<span className="text-muted">
										{t('menu_changed_at')}: {formatDate(data.menu_changed_at, 'dd.MM.yyyy HH:mm')}
									</span>
								}
							</div>
							<div className="table-responsive">
								<table className="table table-bordered mb-0">
									<thead>
										<tr>
											<th style={{ 'width': '33px', 'minWidth': '33px' }} className="text-center">#</th>
											<th>{t('name2')}</th>
											<th className="text-center">{t('menu_pulled_at')}
												{/* расшифровка цвета ячейки — образцами цветов, нативный title их не покажет */}
												<OverlayTrigger placement="top" overlay={
													<Tooltip id="menu-pull-colors">
														<div className="text-start">
															{[['bg-success', 'menu_up_to_date'], ['bg-warning', 'menu_changed_after_pull'], ['bg-secondary', 'menu_never_pulled']].map(([color, key]) => (
																<div key={key} className="d-flex align-items-center gap-2 text-nowrap">
																	<span className={color} style={{ width: '12px', height: '12px', borderRadius: '2px', display: 'inline-block' }}></span>
																	{t(key)}
																</div>
															))}
														</div>
													</Tooltip>
												}>
													<i className="uil-question-circle ms-1" style={{ fontSize: '14px', color: 'var(--dark-text-muted, #98a6ad)', cursor: 'help', verticalAlign: 'middle' }}></i>
												</OverlayTrigger>
											</th>
											<th className="text-center">{t('availability_pulled_at')}</th>
											<th className="text-center">{t('promos_pulled_at')}</th>
											<th style={{ 'width': '1%' }}></th>
										</tr>
									</thead>
									<tbody>
										{data?.poses?.map((item, index) => (
											<tr key={index}>
												<td style={{ 'width': '33px' }} className="text-center">{item.id}</td>
												<td className="text-nowrap">{item.name}</td>
												{/* статус выгрузки — заливкой ячейки «Меню забрали», подробности в title */}
												<td className={`text-center text-nowrap ${returnClassPullStatus(item)}`}
													title={menuPullTitle(item)}>
													{item.menu_pulled_at
														? <>
															{formatDate(item.menu_pulled_at, 'dd.MM HH:mm')}
															{item.menu_pulled_items !== null && item.menu_pulled_items !== undefined &&
																<span className="opacity-75"> · {item.menu_pulled_items} {t('pcs_short')}</span>
															}
														</>
														: '—'
													}
												</td>
												<td className="text-center text-nowrap">
													{item.availability_pulled_at ? formatDate(item.availability_pulled_at, 'dd.MM HH:mm') : '—'}
												</td>
												<td className="text-center text-nowrap">
													{item.promos_pulled_at ? formatDate(item.promos_pulled_at, 'dd.MM HH:mm') : '—'}
												</td>
												<td className="text-end">
													<div className="form-check form-switch form-switch-sm d-inline-block mb-0">
														<input type="checkbox" className="form-check-input"
															checked={item.status ? true : false}
															onChange={(e) => handleSettingChange(e, item, index)} />
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							<div className="d-none" style={{ 'height': '500px', 'overflow': 'auto' }}>
								<button className="btn btn-primary w-100">Проверка</button>
								{renderJson()}
							</div>
						</div>
					</div>

					<div className="btn-wrapper gap-2">
						<Link className="btn btn-warning btn-rounded btn-wide" to="/external-integrations">
							{t('cancel')}
						</Link>
						<button type="button" className="btn btn-primary btn-rounded btn-wide"
							onClick={(e) => createUpdate(e)}>
							{t('save')}
						</button>
					</div>
				</div>
			</div>

			<Modal show={modalRefresh} animation={false}
				centered dialogClassName="update-modal-width"
				onHide={() => toggleModalRefresh()}>
				<Modal.Header closeButton>
					<Modal.Title>{t('delete')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{t('are_you_sure_you_want_delete')}
				</Modal.Body>
				<Modal.Footer >
					<div className="d-flex gap-2 w-100">
						<button className="btn btn-warning w-100"
							onClick={() => toggleModalRefresh()}>
							{t('cancel')}
						</button>
						<button className="btn btn-danger w-100"
							onClick={() => refreshOauthAccessToken()}>{t('delete')}</button>
					</div>
				</Modal.Footer>
			</Modal>
		</>
	)
}

export default Index